import React, { useState, useEffect, useMemo } from "react";
import { Card, CardBody, CardHeader, Spinner } from "reactstrap";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";
import userServices from "services/userServices";

dayjs.extend(relativeTime);

const getSenderPaths = (t) => [
  { path: "fromPlace.name", label: t("page.listing.from", "From"), image: false },
  { path: "toPlace.name", label: t("page.listing.to", "To"), image: false },
  { path: "date", label: t("page.listing.date", "Date"), image: false, format: (v) => v ? helpers.getFormattedDate(v, t) : null },
  { path: "packageDetails.weight", label: t("modal.packageDetails.weightLabel", "Packages Weight"), image: false },
  { path: "packageDetails.packagesNumber", label: t("modal.packageDetails.countLabel", "Number of Packages"), image: false },
  {
    path: "packageDetails.packageLength",
    label: t("modal.packageDetails.dimensionLabel", "Package Dimensions"),
    image: false,
    format: (v, state) => {
      const p = state?.packageDetails;
      if (!p?.packageLength && !p?.packageWidth && !p?.packageHeight) return null;
      return `${p?.packageLength || 0} × ${p?.packageWidth || 0} × ${p?.packageHeight || 0} cm`;
    },
  },
  { path: "packageDetails.itemsDetails", label: t("modal.packageDetails.detailsLabel", "Package details"), image: false },
  { path: "packageDetails.otherDetails", label: t("modal.packageDetails.otherDetailsLabel", "Other details"), image: false },
  { path: "packageDetails.packageImages", label: t("page.orders.packageDetails.packageImages", "Package Images"), image: true },
  {
    path: "packageDetails.price", label: t("table.headers.price", "Price"), image: false, format: (v, state) => {
      if (v === null || v === undefined) return null;
      const currency = state?.packageDetails?.currency;
      const symbol = (typeof currency === 'object' ? currency?.symbol : null) || "";
      const code = (typeof currency === 'object' ? currency?.code : null) || "";
      return `${symbol}${v}${code ? ` (${code})` : ""}`;
    }
  },
  { path: "packageDetails.currency.code", label: t("page.listing.currency", "Currency"), image: false },
  { path: "receiverDetails.name", label: t("page.orders.receiverDetails.name", "Name"), image: false },
  { path: "receiverDetails.email", label: t("page.orders.receiverDetails.email", "Email"), image: false },
  { path: "receiverDetails.contactNumber", label: t("page.orders.receiverDetails.phone", "Contact Number"), image: false },
  { path: "receiverDetails.receiverPhoto", label: t("page.orders.receiverDetails.receiverPhoto", "Receiver Photo"), image: true },
  { path: "pickUpDetails.pickup", label: t("page.create-listing.pickupDetails.pickup.title", "Pickup Location"), image: false },
  { path: "pickUpDetails.pickupAirportName", label: t("page.create-listing.pickupDetails.pickup.airportName", "Airport Name"), image: false },
  { path: "pickUpDetails.pickupGateNumber", label: t("page.create-listing.pickupDetails.pickup.gateNumber", "Terminal"), image: false },
  { path: "pickUpDetails.pickupLandmark", label: t("page.create-listing.pickupDetails.pickup.landmark", "Landmark (Optional)"), image: false },
  { path: "pickUpDetails.drop", label: t("page.create-listing.pickupDetails.drop.title", "Drop Location"), image: false },
  { path: "pickUpDetails.dropAirportName", label: t("page.create-listing.pickupDetails.drop.airportName", "Airport Name"), image: false },
  { path: "pickUpDetails.dropGateNumber", label: t("page.create-listing.pickupDetails.drop.gateNumber", "Terminal"), image: false },
  { path: "pickUpDetails.dropLandmark", label: t("page.create-listing.pickupDetails.drop.landmark", "Landmark (Optional)"), image: false },
];

const getTravelerPaths = (t) => [
  { path: "fromPlace.name", label: t("page.listing.from", "From"), image: false },
  { path: "toPlace.name", label: t("page.listing.to", "To"), image: false },
  { path: "date", label: t("page.listing.date", "Date"), image: false, format: (v) => v ? helpers.getFormattedDate(v, t) : null },
  { path: "baggageDetails.weight", label: t("page.orders.baggageDetails.weight", "Weight"), image: false },
  { path: "baggageDetails.size", label: t("page.orders.baggageDetails.size", "Size"), image: false },
  { path: "baggageDetails.itemCanBeTaken", label: t("page.orders.baggageDetails.itemsAllowed", "Items Allowed"), image: false },
  { path: "baggageDetails.itemCanNotBeTaken", label: t("page.orders.baggageDetails.itemsNotAllowed", "Items Not Allowed"), image: false },
  { 
    path: "baggageDetails.categoriesWillCarry", 
    label: t("page.create-listing.baggageDetails.categoriesTitle", "What I Can Carry"), 
    image: false,
    format: (v) => {
      try {
        const arr = typeof v === 'string' ? (v ? JSON.parse(v) : []) : (Array.isArray(v) ? v : []);
        return arr.length ? arr.join(", ") : null;
      } catch { return String(v || ""); }
    }
  },
  { 
    path: "baggageDetails.restrictionsWillNotCarry", 
    label: t("page.create-listing.baggageDetails.restrictionsTitle", "What I Will NOT Carry"), 
    image: false,
    format: (v) => {
      try {
        const arr = typeof v === 'string' ? (v ? JSON.parse(v) : []) : (Array.isArray(v) ? v : []);
        return arr.length ? arr.join(", ") : null;
      } catch { return String(v || ""); }
    }
  },
  {
    path: "baggageDetails.price", label: t("table.headers.price", "Price"), image: false, format: (v, state) => {
      if (v === null || v === undefined) return null;
      const currency = state?.baggageDetails?.currency;
      const symbol = (typeof currency === 'object' ? currency?.symbol : null) || "";
      const code = (typeof currency === 'object' ? currency?.code : null) || "";
      return `${symbol}${v}${code ? ` (${code})` : ""}`;
    }
  },
  { path: "baggageDetails.currency.code", label: t("page.listing.currency", "Currency"), image: false },
  { path: "documentDetails.travelType", label: t("page.create-listing.documentDetails.travelType", "Travel Type"), image: false },
  { path: "documentDetails.pnrNumber", label: t("page.create-listing.documentDetails.pnrNumber", "PNR Number"), image: false },
  { path: "documentDetails.travelTime", label: t("page.create-listing.documentDetails.travelTime", "Departure Time"), image: false, format: (v) => v ? dayjs(v).format("hh:mm A") : null },
  { path: "departureTimezone", label: t("page.create-listing.documentDetails.timezone", "Time Zone"), image: false, format: (v) => helpers.formatTimezone(v) },
  { path: "documentDetails.departureTimezone", label: t("page.create-listing.documentDetails.timezone", "Time Zone"), image: false, format: (v) => helpers.formatTimezone(v) },
  { path: "documentDetails.ticketImage", label: t("page.orders.documentDetails.ticketImage", "Ticket Image"), image: true },
  { path: "documentDetails.passportImage", label: t("page.orders.documentDetails.passportImage", "Passport Image"), image: true },
  { path: "pickUpDetails.pickup", label: t("page.create-listing.pickupDetails.pickup.title", "Pickup Location"), image: false },
  { path: "pickUpDetails.pickupAirportName", label: t("page.create-listing.pickupDetails.pickup.airportName", "Airport Name"), image: false },
  { path: "pickUpDetails.pickupGateNumber", label: t("page.create-listing.pickupDetails.pickup.gateNumber", "Terminal"), image: false },
  { path: "pickUpDetails.pickupLandmark", label: t("page.create-listing.pickupDetails.pickup.landmark", "Landmark (Optional)"), image: false },
  { path: "pickUpDetails.drop", label: t("page.create-listing.pickupDetails.drop.title", "Drop Location"), image: false },
  { path: "pickUpDetails.dropAirportName", label: t("page.create-listing.pickupDetails.drop.airportName", "Airport Name"), image: false },
  { path: "pickUpDetails.dropGateNumber", label: t("page.create-listing.pickupDetails.drop.gateNumber", "Terminal"), image: false },
  { path: "pickUpDetails.dropLandmark", label: t("page.create-listing.pickupDetails.drop.landmark", "Landmark (Optional)"), image: false },
];

function getByPath(obj, path) {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

function serialize(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return JSON.stringify([...value].sort());
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value) || typeof value === "object") return null;
  const str = String(value);
  return str.length > 35 ? str.slice(0, 35) + "…" : str;
}

function diffStates(oldState, newState, paths) {
  const changed = [];
  for (const field of paths) {
    const oldRaw = getByPath(oldState, field.path);
    const newRaw = getByPath(newState, field.path);
    const oldVal = field.format ? field.format(oldRaw, oldState) : oldRaw;
    const newVal = field.format ? field.format(newRaw, newState) : newRaw;

    if (serialize(oldVal) !== serialize(newVal)) {
      const fmt = field.format || formatValue;
      changed.push({ path: field.path, label: field.label, image: field.image, oldDisplay: fmt(oldRaw, oldState), newDisplay: fmt(newRaw, newState) });
    }
  }
  return changed;
}

function FieldRow({ label, oldDisplay, newDisplay, image, imageUpdatedLabel }) {
  if (image) {
    return (
      <p className="heading-sm mb-1">
        <strong>{label}:</strong>{" "}
        <span className="text-muted">{imageUpdatedLabel}</span>
      </p>
    );
  }
  return (
    <p className="heading-sm mb-1">
      <strong>{label}:</strong>{" "}
      {oldDisplay && newDisplay ? (
        <>
          <span className="text-muted" style={{ textDecoration: "line-through" }}>
            {oldDisplay}
          </span>
          {" → "}
          <span className="text-dark font-weight-bold">{newDisplay}</span>
        </>
      ) : (
        <span className="text-muted">{oldDisplay || newDisplay || "—"}</span>
      )}
    </p>
  );
}

function HistoryEntry({ entry, editLabel, imageUpdatedLabel, isLast }) {
  const { version, changedFields, timestamp, formattedDate } = entry;
  return (
    <div
      className="d-flex"
      style={{
        paddingBottom: "16px",
        marginBottom: isLast ? 0 : "16px",
        borderBottom: isLast ? "none" : "1px solid #f0f2f5",
      }}
    >
      <div className="d-flex align-items-start mr-3" style={{ flexShrink: 0 }}>
        <div
          className="rounded-circle bg-warning"
          style={{ width: "10px", height: "10px", marginTop: "5px", flexShrink: 0 }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="d-flex justify-content-between align-items-baseline mb-2">
          <span className="heading-sm font-weight-bold text-dark" style={{ flexShrink: 0 }}>
            {editLabel} {version - 1}
            <span className="font-weight-normal text-muted ml-2">
              · {dayjs(timestamp).fromNow()}
            </span>
          </span>
          <span className="text-muted ml-2" style={{ fontSize: "11px", flexShrink: 0, whiteSpace: "nowrap" }}>
            {formattedDate}
          </span>
        </div>
        <div>
          {changedFields.map((f, i) => (
            <FieldRow key={i} {...f} imageUpdatedLabel={imageUpdatedLabel} />
          ))}
        </div>
      </div>
    </div>
  );
}

const LIMIT = 5;

export default function EditHistoryCard({ listingId, currentListing, listingType, onLatestChanges }) {
  const { t } = useTranslation();
  const [rawHistory, setRawHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!listingId || !listingType) {
      setLoading(false);
      return;
    }
    setPage(1);
    setHasMore(false);
    setTotalCount(null);
    setRawHistory([]);
    setError(null);
    setLoading(true);
    userServices
      .getListingHistory(listingId, listingType, { page: 1, limit: LIMIT })
      .then((res) => {
        if (!res?.data?.success) return;
        setRawHistory(res.data.data);
        if (res.data.pagination) {
          setTotalCount(res.data.pagination.total);
          setHasMore(res.data.pagination.totalPages > 1);
        }
      })
      .catch((err) => {
        console.error("EditHistoryCard: failed to load history", err);
        setError("loadError");
      })
      .finally(() => setLoading(false));
  }, [listingId, listingType]);

  const loadMore = () => {
    const nextPage = page + 1;
    setError(null)
    setLoadingMore(true);
    userServices
      .getListingHistory(listingId, listingType, { page: nextPage, limit: LIMIT })
      .then((res) => {
        if (!res?.data?.success) return;
        setRawHistory((prev) => [...prev, ...res.data.data]);
        setPage(nextPage);
        setHasMore(res.data.pagination ? nextPage < res.data.pagination.totalPages : false);
      })
      .catch((err) => {
        console.error("EditHistoryCard: failed to load more history", err);
        setError("loadMoreError");
      })
      .finally(() => setLoadingMore(false));
  };

  const entries = useMemo(() => {
    if (!rawHistory.length) return [];
    const isSender = listingType?.toLowerCase() === "sender";
    const paths = isSender ? getSenderPaths(t) : getTravelerPaths(t);

    return rawHistory
      .map((snapshot, i) => {
        // Compare with previous version (i + 1 in rawHistory array)
        const prevState = rawHistory[i + 1]?.state || {};
        const changedFields = diffStates(prevState, snapshot.state, paths);

        return {
          version: snapshot.version,
          timestamp: snapshot.createdAt,
          formattedDate: `${helpers.getFormattedDate(snapshot.createdAt, t)} · ${dayjs(snapshot.createdAt).format("h:mm A")}`,
          changedFields,
        };
      })
      .filter((entry) => entry.changedFields.length > 0 && entry.version > 1);
  }, [rawHistory, listingType, t]);

  const aggregatedHighlights = useMemo(() => {
    if (rawHistory.length < 1) return [];

    const isSender = listingType?.toLowerCase() === "sender";
    const paths = isSender ? getSenderPaths(t) : getTravelerPaths(t);

    const latestEntry = rawHistory[0];
    const latestState = latestEntry.state || {};

    const baseEntry = rawHistory[1];

    if (!baseEntry || baseEntry === latestEntry) return [];

    const changes = diffStates(baseEntry.state || {}, latestState, paths);
    return changes.map(c => c.path);
  }, [rawHistory, listingType, t]);

  useEffect(() => {
    if (onLatestChanges) {
      onLatestChanges(aggregatedHighlights);
    }
  }, [aggregatedHighlights, onLatestChanges]);

  if (loading) {
    return (
      <Card className="card-profile shadow border-0 mb-3">
        <CardBody className="py-3 px-3 text-center">
          <Spinner size="sm" color="warning" />
        </CardBody>
      </Card>
    );
  }

  if (!entries.length) return null;

  return (
    <Card className="card-profile shadow border-0 mb-3">
      <CardHeader className="bg-transparent border-0 pb-0 pt-3 px-3">
        <div className="d-flex align-items-center justify-content-between">
          <h4 className="heading-md text-muted mb-0">
            {t("page.listing.editHistory.title", "Edit History")}
          </h4>
          <span className="badge badge-warning badge-pill" style={{ fontSize: "10px" }}>
            {totalCount ? Math.max(0, totalCount - 1) : entries.length}
          </span>
        </div>
      </CardHeader>

      <CardBody className="pt-3 pb-2 px-3">
        <div style={{ overflowY: "auto", overflowX: "hidden", maxHeight: "400px", paddingRight: "8px", marginRight: "-8px" }}>
          {entries.map((entry, i) => (
            <HistoryEntry
              key={entry.version}
              entry={entry}
              editLabel={t("page.listing.editHistory.editLabel", "Edit")}
              imageUpdatedLabel={t("page.listing.editHistory.imageUpdated", "updated")}
              isLast={i === entries.length - 1 && !hasMore}
            />
          ))}

          {hasMore && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mt-1 text-warning font-weight-bold"
              style={{ fontSize: "12px", textDecoration: "none" }}
              disabled={loadingMore}
              onClick={loadMore}
            >
              {loadingMore
                ? <Spinner size="sm" color="warning" />
                : t("page.listing.editHistory.loadMore", "Load more")}
            </button>
          )}
          {error && (
            <p className="text-danger heading-sm mt-1 mb-0" style={{ fontSize: "12px" }}>
              {error === "loadError" && t("page.listing.editHistory.loadError", "Failed to load edit history.")}
              {error === "loadMoreError" && t("page.listing.editHistory.loadMoreError", "Failed to load more. Please try again.")}
            </p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
